// src/components/dashboard/ai/FlowSenseIntelContext.js
// ─────────────────────────────────────────────────────────────────────────────
// One snapshot + one live activity feed for the whole dashboard.
// DashboardPage wraps everything in <FlowSenseIntelProvider>; the AI widgets read it
// with useFlowSenseIntel(). A widget rendered outside the provider loads its own
// snapshot (the hook is disabled when the provider exists, so nothing runs twice).
// ─────────────────────────────────────────────────────────────────────────────
import React, { createContext, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import useFlowSenseSnapshot from '../../../hooks/useFlowSenseSnapshot';
import { useFlowSenseEventFeed } from '../../../hooks/useFlowSenseLive';

const IntelContext = createContext(null);

export function FlowSenseIntelProvider({ children, enabled = true }) {
    const snap = useFlowSenseSnapshot({ enabled });
    const feed = useFlowSenseEventFeed({ max: 40 });
    const value = useMemo(() => ({ ...snap, feed }), [snap, feed]);
    return <IntelContext.Provider value={value}>{children}</IntelContext.Provider>;
}
FlowSenseIntelProvider.propTypes = { children: PropTypes.node, enabled: PropTypes.bool };

/** Snapshot, brief and live feed — from the provider, or loaded locally as a fallback. */
export function useFlowSenseIntel() {
    const ctx = useContext(IntelContext);
    const own = useFlowSenseSnapshot({ enabled: !ctx });
    const ownFeed = useFlowSenseEventFeed({ max: ctx ? 1 : 40, groups: ctx ? [] : undefined });
    return ctx || { ...own, feed: ownFeed };
}

export default IntelContext;
